/**
 * Postal-code (código postal / zip) validation shared by every address form and
 * brand. Mirrors the two-layer approach of `vatValidation`:
 *  1. Per-country format regex, when one is registered in `postalCodeValidators`.
 *  2. A permissive generic fallback for countries without a specific format
 *     (add them here as they are implemented).
 *
 * The value is trimmed first, so a whitespace-only entry counts as empty (this
 * removes the old inconsistency where "   " passed the format check but was
 * treated as empty at submit time).
 */

/**
 * Per-country postal-code formats, keyed by the same country key used by
 * `countryData` (e.g. 'PT', 'ES', 'GB').
 *
 * NOTE: 'GB' is listed for completeness but is currently unreachable — the
 * country selector is populated from `countryData`, which has no GB entry, so
 * the address country can never be GB until UK support is added.
 */
export const postalCodeValidators: Record<string, RegExp> = {
  PT: /^\d{4}-\d{3}$/,
  ES: /^\d{5}$/,
  GB: /^[A-Za-z]{1,2}\d[A-Za-z\d]? ?\d[A-Za-z]{2}$/,
};

/** Loose length + charset check for countries without a specific format. */
export const GENERIC_POSTAL_CODE = /^[A-Za-z0-9\- ]{3,10}$/;

export type PostalCodeValidationOptions = { required?: boolean };

/**
 * Validates a postal code for the given country. Returns an i18n KEY describing
 * the error, or null when the value is valid.
 *  - empty + required       → 'required_zip_code'
 *  - fails the format check  → 'invalid_zip_code'
 *
 * @param country country key (e.g. 'PT'); typically the address country state
 * @param value   the postal code the user typed
 */
export const getPostalCodeError = (
  country: string | null | undefined,
  value: string | null | undefined,
  { required = true }: PostalCodeValidationOptions = {},
): string | null => {
  const trimmed = (value || '').trim();
  if (!trimmed) return required ? 'required_zip_code' : null;

  const validator = postalCodeValidators[country || ''] || GENERIC_POSTAL_CODE;
  return validator.test(trimmed) ? null : 'invalid_zip_code';
};

/** Convenience boolean wrapper around {@link getPostalCodeError}. */
export const isPostalCodeValid = (
  country: string | null | undefined,
  value: string | null | undefined,
  options?: PostalCodeValidationOptions,
): boolean => getPostalCodeError(country, value, options) === null;
